import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import Header from "../components/Header";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import { 
  Copy, 
  Check, 
  Terminal, 
  Code, 
  Zap, 
  BookOpen,
  ArrowRight,
  ExternalLink,
  Key,
  FolderPlus,
  Send,
  BarChart3
} from "lucide-react";
import { toast } from "sonner";

const pythonSDK = `import requests
import random
from typing import Optional, Dict, Any, List

class RAGDebugger:
    def __init__(
        self,
        api_key: str,
        project_id: str,
        base_url: str,
        environment: str = "development",
        sample_rate: float = 1.0,
    ):
        """
        Initialize RAG Debugger SDK
        
        Args:
            api_key: Your API key from the dashboard (starts with rdb_)
            project_id: Your project ID from the dashboard
            base_url: Your backend URL + /api
            environment: 'development', 'staging', or 'production'
            sample_rate: Fraction of traces to send (0.0 to 1.0)
        """
        self.api_key = api_key
        self.project_id = project_id
        self.base_url = base_url.rstrip('/')
        self.environment = environment
        self.sample_rate = sample_rate

    def trace(
        self,
        user_query: str,
        retrieved_chunks: List[Dict[str, Any]],
        constructed_prompt: str,
        model_response: str,
        model_name: str = "unknown",
        latency_ms: int = 0,
        token_count: int = 0,
        metadata: Optional[Dict[str, Any]] = None,
    ) -> Optional[str]:
        """
        Send a trace to RAG Debugger
        
        Returns:
            Trace ID if successful, None otherwise
        """
        # Sampling
        if random.random() > self.sample_rate:
            return None

        # Format chunks
        formatted_chunks = []
        for chunk in retrieved_chunks:
            formatted_chunks.append({
                "content": chunk.get("content", ""),
                "score": chunk.get("score", 0.0),
                "metadata": chunk.get("metadata", {})
            })

        payload = {
            "project_id": self.project_id,
            "environment": self.environment,
            "user_query": user_query,
            "retrieved_chunks": formatted_chunks,
            "constructed_prompt": constructed_prompt,
            "model_response": model_response,
            "model_name": model_name,
            "latency_ms": latency_ms,
            "token_count": token_count,
            "metadata": metadata or {}
        }

        try:
            response = requests.post(
                f"{self.base_url}/v1/traces",
                json=payload,
                headers={
                    "X-API-Key": self.api_key,
                    "Content-Type": "application/json"
                },
                timeout=10
            )
            response.raise_for_status()
            return response.json().get("id")
        except Exception as e:
            print(f"RAG Debugger: Failed to send trace - {e}")
            return None`;

const pythonUsage = `from rag_debugger import RAGDebugger
import time

# Initialize the debugger (do this once)
debugger = RAGDebugger(
    api_key="YOUR_API_KEY",              # Get from Dashboard
    project_id="YOUR_PROJECT_ID",        # Get from URL: /projects/THIS_PART (NOT the project name!)
    base_url="https://your-rag-debugger.railway.app/api",
    environment="production"
)

# Your existing RAG code
user_query = "What is machine learning?"
start_time = time.time()

# Step 1: Retrieve chunks (your existing code)
chunks = retriever.search(user_query)

# Step 2: Build prompt (your existing code)
context = "\\n".join([c['content'] for c in chunks])
prompt = f"""Based on the context below, answer the question.

Context:
{context}

Question: {user_query}
"""

# Step 3: Call LLM (your existing code)
response = llm.generate(prompt)

latency_ms = int((time.time() - start_time) * 1000)

# Step 4: Send trace to RAG Debugger (ADD THIS!)
debugger.trace(
    user_query=user_query,
    retrieved_chunks=[
        {
            "content": c["content"], 
            "score": c["score"], 
            "metadata": {"source": c.get("source", "")}
        }
        for c in chunks
    ],
    constructed_prompt=prompt,
    model_response=response,
    model_name="gpt-4",
    latency_ms=latency_ms
)`;

const langchainExample = `from rag_debugger import RAGDebugger
from langchain.chains import RetrievalQA
from langchain.chat_models import ChatOpenAI
from langchain.vectorstores import Chroma
import time

# Initialize debugger
debugger = RAGDebugger(
    api_key="YOUR_API_KEY",
    project_id="YOUR_PROJECT_ID",
    base_url="https://your-rag-debugger.railway.app/api",
    environment="production"
)

# Your LangChain setup
vectorstore = Chroma(...)
retriever = vectorstore.as_retriever()
llm = ChatOpenAI(model="gpt-4")

def query_with_tracing(query: str) -> str:
    start = time.time()
    
    # Get relevant documents
    docs = retriever.get_relevant_documents(query)
    
    # Build context
    context = "\\n\\n".join([doc.page_content for doc in docs])
    prompt = f"Context:\\n{context}\\n\\nQuestion: {query}"
    
    # Get response
    response = llm.predict(prompt)
    
    # Send trace
    debugger.trace(
        user_query=query,
        retrieved_chunks=[
            {
                "content": doc.page_content,
                "score": 0.85,  # LangChain doesn't always expose scores
                "metadata": doc.metadata
            }
            for doc in docs
        ],
        constructed_prompt=prompt,
        model_response=response,
        model_name="gpt-4",
        latency_ms=int((time.time() - start) * 1000)
    )
    
    return response

# Use it
answer = query_with_tracing("What are the benefits of RAG?")`;

const llamaIndexExample = `from rag_debugger import RAGDebugger
from llama_index import VectorStoreIndex, SimpleDirectoryReader
import time

# Initialize debugger
debugger = RAGDebugger(
    api_key="YOUR_API_KEY",
    project_id="YOUR_PROJECT_ID",
    base_url="https://your-rag-debugger.railway.app/api",
    environment="production"
)

# Your LlamaIndex setup
documents = SimpleDirectoryReader("data").load_data()
index = VectorStoreIndex.from_documents(documents)
query_engine = index.as_query_engine()

def query_with_tracing(query: str) -> str:
    start = time.time()
    
    # Get response with source nodes
    response = query_engine.query(query)
    
    # Extract chunks from source nodes
    chunks = []
    for node in response.source_nodes:
        chunks.append({
            "content": node.node.text,
            "score": node.score or 0.0,
            "metadata": node.node.metadata
        })
    
    # Send trace
    debugger.trace(
        user_query=query,
        retrieved_chunks=chunks,
        constructed_prompt=f"Query: {query}",  # LlamaIndex handles prompts internally
        model_response=str(response),
        model_name="gpt-3.5-turbo",
        latency_ms=int((time.time() - start) * 1000)
    )
    
    return str(response)

# Use it
answer = query_with_tracing("Summarize the main points")`;

const jsSDK = `import axios from 'axios';

interface RetrievedChunk {
  content: string;
  score: number;
  metadata?: Record<string, any>;
}

interface TraceData {
  userQuery: string;
  retrievedChunks: RetrievedChunk[];
  constructedPrompt: string;
  modelResponse: string;
  modelName?: string;
  latencyMs?: number;
  tokenCount?: number;
  metadata?: Record<string, any>;
}

interface RAGDebuggerConfig {
  apiKey: string;
  projectId: string;
  baseUrl: string;
  environment?: 'development' | 'staging' | 'production';
  sampleRate?: number;
}

class RAGDebugger {
  private config: RAGDebuggerConfig;

  constructor(config: RAGDebuggerConfig) {
    this.config = {
      environment: 'development',
      sampleRate: 1.0,
      ...config
    };
  }

  async trace(data: TraceData): Promise<string | null> {
    if (Math.random() > (this.config.sampleRate || 1.0)) {
      return null;
    }

    try {
      const response = await axios.post(
        \`\${this.config.baseUrl}/v1/traces\`,
        {
          project_id: this.config.projectId,
          environment: this.config.environment,
          user_query: data.userQuery,
          retrieved_chunks: data.retrievedChunks,
          constructed_prompt: data.constructedPrompt,
          model_response: data.modelResponse,
          model_name: data.modelName || 'unknown',
          latency_ms: data.latencyMs || 0,
          token_count: data.tokenCount || 0,
          metadata: data.metadata || {}
        },
        {
          headers: {
            'X-API-Key': this.config.apiKey,
            'Content-Type': 'application/json'
          }
        }
      );
      return response.data.id;
    } catch (error) {
      console.error('RAG Debugger: Failed to send trace', error);
      return null;
    }
  }
}

export default RAGDebugger;`;

const jsUsage = `import RAGDebugger from './rag-debugger';

// Initialize (do this once)
const debugger = new RAGDebugger({
  apiKey: 'YOUR_API_KEY',
  projectId: 'YOUR_PROJECT_ID',
  baseUrl: 'https://your-rag-debugger.railway.app/api',
  environment: 'production'
});

// Your existing RAG function
async function askQuestion(query: string) {
  const startTime = Date.now();
  
  // Step 1: Retrieve chunks (your existing code)
  const chunks = await retriever.search(query);
  
  // Step 2: Build prompt (your existing code)
  const prompt = \`Based on the context, answer: \${query}
  
Context:
\${chunks.map(c => c.content).join('\\n')}\`;
  
  // Step 3: Call LLM (your existing code)
  const response = await llm.generate(prompt);
  
  // Step 4: Send trace (ADD THIS!)
  await debugger.trace({
    userQuery: query,
    retrievedChunks: chunks.map(c => ({
      content: c.content,
      score: c.score,
      metadata: { source: c.source }
    })),
    constructedPrompt: prompt,
    modelResponse: response,
    modelName: 'gpt-4',
    latencyMs: Date.now() - startTime
  });
  
  return response;
}`;

const curlExample = `# Send a trace via cURL
curl -X POST "https://your-rag-debugger.railway.app/api/v1/traces" \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -d '{
    "project_id": "YOUR_PROJECT_ID",
    "environment": "production",
    "user_query": "What is machine learning?",
    "retrieved_chunks": [
      {
        "content": "Machine learning is a subset of AI...",
        "score": 0.92,
        "metadata": {"source": "wiki"}
      }
    ],
    "constructed_prompt": "Based on context, answer: What is ML?",
    "model_response": "Machine learning is...",
    "model_name": "gpt-4",
    "latency_ms": 1200
  }'`;

export default function DocsPage() {
  const { user } = useAuth();
  const [copiedSection, setCopiedSection] = useState(null);

  const copyToClipboard = (text, section) => {
    // Replace placeholders with actual values if available
    let processedText = text;
    if (user?.api_key) {
      processedText = processedText.replace(/YOUR_API_KEY/g, user.api_key);
    }
    
    navigator.clipboard.writeText(processedText);
    setCopiedSection(section);
    setTimeout(() => setCopiedSection(null), 2000);
    toast.success("Copied to clipboard!");
  };

  const CodeBlock = ({ code, language, section }) => (
    <div className="relative group">
      <Button
        variant="ghost"
        size="sm"
        className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
        onClick={() => copyToClipboard(code, section)}
      >
        {copiedSection === section ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
      </Button>
      <pre className="bg-slate-950 text-slate-50 p-4 rounded-sm text-sm font-mono overflow-x-auto">
        <code>{code}</code>
      </pre>
    </div>
  );

  return (
    <div className="min-h-screen bg-background" data-testid="docs-page">
      <Header />
      
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
            <Link to="/dashboard" className="hover:text-foreground">Dashboard</Link>
            <span>/</span>
            <span>Documentation</span>
          </div>
          <h1 className="text-3xl font-black tracking-tight font-heading flex items-center gap-3">
            <BookOpen className="w-8 h-8" />
            Integration Guide
          </h1>
          <p className="text-muted-foreground mt-2">
            Learn how to send traces from your RAG application to the debugger dashboard.
          </p>
        </div>

        {/* Quick Start Steps */}
        <Card className="mb-8 border border-border shadow-none">
          <CardHeader>
            <CardTitle className="text-xl font-bold flex items-center gap-2">
              <Zap className="w-5 h-5 text-yellow-500" />
              Quick Start (3 Steps)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="flex items-start gap-3 p-4 border border-border rounded-sm">
                <div className="w-8 h-8 rounded-full bg-foreground text-background flex items-center justify-center font-bold text-sm flex-shrink-0">1</div>
                <div>
                  <h3 className="font-semibold flex items-center gap-2">
                    <Key className="w-4 h-4" />
                    Get API Key
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Copy your API key from the <Link to="/dashboard" className="text-blue-600 hover:underline">Dashboard</Link>
                  </p>
                  {user?.api_key && (
                    <code className="text-xs bg-muted px-2 py-1 rounded mt-2 block truncate">
                      {user.api_key}
                    </code>
                  )}
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 border border-border rounded-sm">
                <div className="w-8 h-8 rounded-full bg-foreground text-background flex items-center justify-center font-bold text-sm flex-shrink-0">2</div>
                <div>
                  <h3 className="font-semibold flex items-center gap-2">
                    <FolderPlus className="w-4 h-4" />
                    Create Project
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Create a project in the dashboard to organize your traces
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 border border-border rounded-sm">
                <div className="w-8 h-8 rounded-full bg-foreground text-background flex items-center justify-center font-bold text-sm flex-shrink-0">3</div>
                <div>
                  <h3 className="font-semibold flex items-center gap-2">
                    <Send className="w-4 h-4" />
                    Send Traces
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Add the SDK to your code and call <code className="text-xs bg-muted px-1 rounded">trace()</code>
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Configuration */}
        <Card className="mb-8 border border-border shadow-none">
          <CardHeader>
            <CardTitle className="text-xl font-bold">Configuration Values</CardTitle>
            <CardDescription>You'll need these values to initialize the SDK</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-2 pr-4 font-semibold">Parameter</th>
                    <th className="text-left py-2 pr-4 font-semibold">Description</th>
                    <th className="text-left py-2 font-semibold">Your Value</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-border">
                    <td className="py-3 pr-4"><code className="bg-muted px-2 py-0.5 rounded text-xs">api_key</code></td>
                    <td className="py-3 pr-4 text-muted-foreground">Your authentication key</td>
                    <td className="py-3">
                      <code className="bg-muted px-2 py-0.5 rounded text-xs">{user?.api_key || 'Get from Dashboard'}</code>
                    </td>
                  </tr>
                  <tr className="border-b border-border">
                    <td className="py-3 pr-4"><code className="bg-muted px-2 py-0.5 rounded text-xs">project_id</code></td>
                    <td className="py-3 pr-4 text-muted-foreground">
                      MongoDB ObjectId of your project<br/>
                      <span className="text-xs text-red-600">(NOT the project name!)</span>
                    </td>
                    <td className="py-3">
                      <code className="bg-muted px-2 py-0.5 rounded text-xs">From URL: /projects/<strong>67e912abc123...</strong></code>
                    </td>
                  </tr>
                  <tr className="border-b border-border">
                    <td className="py-3 pr-4"><code className="bg-muted px-2 py-0.5 rounded text-xs">base_url</code></td>
                    <td className="py-3 pr-4 text-muted-foreground">
                      RAG Debugger API URL<br/>
                      <span className="text-xs text-green-600">(Same for all users - this is the debugger service URL)</span>
                    </td>
                    <td className="py-3">
                      <code className="bg-muted px-2 py-0.5 rounded text-xs">{process.env.REACT_APP_BACKEND_URL || 'https://your-rag-debugger-backend.railway.app'}/api</code>
                    </td>
                  </tr>
                  <tr>
                    <td className="py-3 pr-4"><code className="bg-muted px-2 py-0.5 rounded text-xs">environment</code></td>
                    <td className="py-3 pr-4 text-muted-foreground">deployment environment</td>
                    <td className="py-3">
                      <code className="bg-muted px-2 py-0.5 rounded text-xs">'development' | 'staging' | 'production'</code>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Language Tabs */}
        <Card className="border border-border shadow-none">
          <CardHeader>
            <CardTitle className="text-xl font-bold flex items-center gap-2">
              <Code className="w-5 h-5" />
              SDK Integration
            </CardTitle>
            <CardDescription>Choose your language and follow the steps</CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="python" className="w-full">
              <TabsList className="mb-4">
                <TabsTrigger value="python" className="flex items-center gap-2">
                  <Terminal className="w-4 h-4" />
                  Python
                </TabsTrigger>
                <TabsTrigger value="javascript" className="flex items-center gap-2">
                  <Code className="w-4 h-4" />
                  JavaScript/TypeScript
                </TabsTrigger>
                <TabsTrigger value="curl" className="flex items-center gap-2">
                  <Terminal className="w-4 h-4" />
                  cURL / REST API
                </TabsTrigger>
              </TabsList>

              {/* Python Tab */}
              <TabsContent value="python" className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold mb-3">Step 1: Install Dependencies</h3>
                  <CodeBlock code="pip install requests" language="bash" section="py-install" />
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-3">Step 2: Create SDK File</h3>
                  <p className="text-sm text-muted-foreground mb-3">
                    Save this as <code className="bg-muted px-2 py-0.5 rounded">rag_debugger.py</code> in your project:
                  </p>
                  <ScrollArea className="h-[400px]">
                    <CodeBlock code={pythonSDK} language="python" section="py-sdk" />
                  </ScrollArea>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-3">Step 3: Use in Your RAG Code</h3>
                  <CodeBlock code={pythonUsage} language="python" section="py-usage" />
                </div>

                <div className="border-t border-border pt-6">
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <Badge variant="outline">Framework Examples</Badge>
                  </h3>
                  
                  <Tabs defaultValue="langchain" className="w-full">
                    <TabsList className="mb-3">
                      <TabsTrigger value="langchain">LangChain</TabsTrigger>
                      <TabsTrigger value="llamaindex">LlamaIndex</TabsTrigger>
                    </TabsList>
                    
                    <TabsContent value="langchain">
                      <CodeBlock code={langchainExample} language="python" section="py-langchain" />
                    </TabsContent>
                    
                    <TabsContent value="llamaindex">
                      <CodeBlock code={llamaIndexExample} language="python" section="py-llamaindex" />
                    </TabsContent>
                  </Tabs>
                </div>
              </TabsContent>

              {/* JavaScript Tab */}
              <TabsContent value="javascript" className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold mb-3">Step 1: Install Dependencies</h3>
                  <CodeBlock code="npm install axios\n# or\nyarn add axios" language="bash" section="js-install" />
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-3">Step 2: Create SDK File</h3>
                  <p className="text-sm text-muted-foreground mb-3">
                    Save this as <code className="bg-muted px-2 py-0.5 rounded">rag-debugger.ts</code> in your project:
                  </p>
                  <ScrollArea className="h-[400px]">
                    <CodeBlock code={jsSDK} language="typescript" section="js-sdk" />
                  </ScrollArea>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-3">Step 3: Use in Your RAG Code</h3>
                  <CodeBlock code={jsUsage} language="typescript" section="js-usage" />
                </div>
              </TabsContent>

              {/* cURL Tab */}
              <TabsContent value="curl" className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold mb-3">REST API Endpoint</h3>
                  <p className="text-sm text-muted-foreground mb-3">
                    You can send traces directly via HTTP POST to the <code className="bg-muted px-2 py-0.5 rounded">/api/v1/traces</code> endpoint.
                  </p>
                  <CodeBlock code={curlExample} language="bash" section="curl" />
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-3">Request Headers</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-border">
                          <th className="text-left py-2 pr-4 font-semibold">Header</th>
                          <th className="text-left py-2 font-semibold">Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-b border-border">
                          <td className="py-2 pr-4"><code className="bg-muted px-2 py-0.5 rounded text-xs">Content-Type</code></td>
                          <td className="py-2"><code className="text-xs">application/json</code></td>
                        </tr>
                        <tr>
                          <td className="py-2 pr-4"><code className="bg-muted px-2 py-0.5 rounded text-xs">X-API-Key</code></td>
                          <td className="py-2"><code className="text-xs">Your API key</code></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-3">Request Body Schema</h3>
                  <CodeBlock 
                    code={`{
  "project_id": "string (required)",
  "environment": "development | staging | production",
  "user_query": "string (required) - The user's question",
  "retrieved_chunks": [
    {
      "content": "string - The chunk text",
      "score": "number - Similarity score (0-1)",
      "metadata": "object - Optional metadata"
    }
  ],
  "constructed_prompt": "string - Full prompt sent to LLM",
  "model_response": "string - LLM's response",
  "model_name": "string - e.g., 'gpt-4'",
  "latency_ms": "number - Response time in ms",
  "token_count": "number - Tokens used",
  "metadata": "object - Any additional data"
}`} 
                    language="json" 
                    section="schema" 
                  />
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* What You'll See */}
        <Card className="mt-8 border border-border shadow-none">
          <CardHeader>
            <CardTitle className="text-xl font-bold flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              What You'll See in the Dashboard
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="p-4 border border-border rounded-sm">
                <h4 className="font-semibold mb-2">Trace List</h4>
                <p className="text-sm text-muted-foreground">
                  All your RAG queries with status badges, issue counts, latency, and timestamps.
                </p>
              </div>
              <div className="p-4 border border-border rounded-sm">
                <h4 className="font-semibold mb-2">Trace Details</h4>
                <p className="text-sm text-muted-foreground">
                  Deep dive into each trace: retrieved chunks, prompts, responses, and AI analysis.
                </p>
              </div>
              <div className="p-4 border border-border rounded-sm">
                <h4 className="font-semibold mb-2">"Why It Failed" Analysis</h4>
                <p className="text-sm text-muted-foreground">
                  AI-powered explanations of what went wrong in plain English.
                </p>
              </div>
              <div className="p-4 border border-border rounded-sm">
                <h4 className="font-semibold mb-2">Fix Suggestions</h4>
                <p className="text-sm text-muted-foreground">
                  Actionable recommendations to improve your RAG pipeline.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Help */}
        <div className="mt-8 text-center text-sm text-muted-foreground">
          <p>
            Need help? Check the{" "}
            <a href="#" className="text-blue-600 hover:underline">API Reference</a>
            {" "}or contact support.
          </p>
        </div>
      </main>
    </div>
  );
}
